/**
 * Telephony Store - State Management для телефонии
 * Управление звонками, записями, WebRTC и PBX статусом
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { telephonyApi } from '../api/telephony.api'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import type {
  Call,
  CallEvent,
  CallsFilter,
  PaginationParams,
  InitiateCallRequest,
  InitiateCallResponse,
  CallRecordingResponse,
  CallTranscription,
  WebRTCKeyResponse,
  ManagerPBXStatus,
  ServicePhone,
} from '../types/telephony.types'

export const useTelephonyStore = defineStore('telephony', () => {
  // State
  const calls = ref<Call[]>([])
  const currentCall = ref<Call | null>(null)
  const activeCallResponse = ref<InitiateCallResponse | null>(null)
  const isLoading = ref(false)
  const isLoadingCalls = ref(false)
  const isInitiatingCall = ref(false)
  const error = ref<string | null>(null)

  // Pagination
  const currentPage = ref(1)
  const pageSize = ref(20)
  const totalCalls = ref(0)
  const totalPages = ref(0)

  // Filters
  const filters = ref<CallsFilter>({})

  // WebRTC
  const webrtcKey = ref<WebRTCKeyResponse | null>(null)

  // Manager PBX
  const managerStatus = ref<ManagerPBXStatus | null>(null)

  // Service Phones
  const servicePhones = ref<ServicePhone[]>([])
  const isLoadingPhones = ref(false)

  // Transcription
  const transcriptionCache = ref<Record<string, CallTranscription | null>>({})
  const isLoadingTranscription = ref(false)

  // Active call state (from WebSocket events)
  const callState = ref<'idle' | 'ringing' | 'answered' | 'ended'>('idle')
  const callPhoneNumber = ref<string>('')
  const callDisposition = ref<string>('')
  const callDuration = ref<number>(0)
  const activeCallClientId = ref<string | null>(null)
  const activeCallClientName = ref<string | null>(null)
  // Текущий звонок прилетел на общий DID (общий пул) — для пометки в попапе (DEV-258)
  const callIsGeneral = ref<boolean>(false)

  // Getters
  const hasCalls = computed(() => calls.value.length > 0)
  const hasMorePages = computed(() => currentPage.value < totalPages.value)
  const isCallActive = computed(
    () =>
      activeCallResponse.value !== null ||
      callState.value === 'ringing' ||
      callState.value === 'answered',
  )
  const hasServicePhones = computed(() => servicePhones.value.length > 0)

  // Actions

  /**
   * Загрузить список звонков с фильтрами и пагинацией
   */
  const loadCalls = async (newFilters?: CallsFilter, page?: number) => {
    isLoadingCalls.value = true
    error.value = null

    try {
      if (newFilters !== undefined) {
        filters.value = newFilters
      }
      if (page !== undefined) {
        currentPage.value = page
      }

      const pagination: PaginationParams = {
        page: currentPage.value,
        pageSize: pageSize.value,
      }

      const result = await telephonyApi.listCalls(filters.value, pagination)

      calls.value = result.calls
      totalCalls.value = result.total
      totalPages.value = result.totalPages
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Не удалось загрузить звонки'
      console.error('[TelephonyStore] loadCalls error:', e)
    } finally {
      isLoadingCalls.value = false
    }
  }

  /**
   * Загрузить звонки по клиенту
   */
  const loadCallsByClient = async (clientId: string, page?: number) => {
    return loadCalls({ clientId }, page)
  }

  /**
   * Инициировать звонок
   */
  const initiateCall = async (
    request: InitiateCallRequest,
  ): Promise<InitiateCallResponse | null> => {
    isInitiatingCall.value = true
    error.value = null

    try {
      const result = await telephonyApi.initiateCall(request)
      activeCallResponse.value = result
      return result
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Не удалось инициировать звонок'
      console.error('[TelephonyStore] initiateCall error:', e)
      return null
    } finally {
      isInitiatingCall.value = false
    }
  }

  /**
   * Получить presigned URL записи звонка
   */
  const getCallRecording = async (callId: string): Promise<CallRecordingResponse | null> => {
    isLoading.value = true
    error.value = null

    try {
      return await telephonyApi.getCallRecording(callId)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Не удалось получить запись'
      console.error('[TelephonyStore] getCallRecording error:', e)
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Получить WebRTC ключ
   */
  const fetchWebRTCKey = async (): Promise<WebRTCKeyResponse | null> => {
    try {
      const domain = window.location.hostname
      const result = await telephonyApi.getWebRTCKey(domain)
      webrtcKey.value = result
      return result
    } catch (e) {
      console.error('[TelephonyStore] fetchWebRTCKey error:', e)
      return null
    }
  }

  /**
   * Получить статус PBX менеджера
   */
  const fetchManagerStatus = async (managerId: string): Promise<ManagerPBXStatus | null> => {
    try {
      const result = await telephonyApi.getManagerPBXStatus(managerId)
      managerStatus.value = result
      return result
    } catch (e) {
      console.error('[TelephonyStore] fetchManagerStatus error:', e)
      return null
    }
  }

  /**
   * Получить транскрипцию звонка
   */
  const getCallTranscription = async (callId: string): Promise<CallTranscription | null> => {
    if (transcriptionCache.value[callId] !== undefined) {
      return transcriptionCache.value[callId]
    }

    isLoadingTranscription.value = true
    try {
      const result = await telephonyApi.getCallTranscription(callId)
      transcriptionCache.value[callId] = result
      return result
    } catch (e) {
      console.error('[TelephonyStore] getCallTranscription error:', e)
      return null
    } finally {
      isLoadingTranscription.value = false
    }
  }

  /**
   * Загрузить список доступных телефонных номеров
   */
  const loadServicePhones = async (): Promise<ServicePhone[]> => {
    isLoadingPhones.value = true
    error.value = null

    try {
      const result = await telephonyApi.listServicePhones()
      servicePhones.value = result
      return result
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Не удалось загрузить список телефонов'
      console.error('[TelephonyStore] loadServicePhones error:', e)
      return []
    } finally {
      isLoadingPhones.value = false
    }
  }

  /**
   * Обработка события звонка от WebSocket
   */
  const handleCallEvent = (event: CallEvent) => {
    console.debug('[TelephonyStore] call event:', event.type, event)

    // Defense-in-depth (DEV-258): обрабатываем только адресованные нам события.
    // Показываем событие, если это общий звонок (isGeneral) ИЛИ оно адресовано
    // текущему менеджеру. Чужие адресные события отбрасываем — иначе всплывёт чужой попап.
    const currentUserId = useAuthStore().user?.id
    if (!event.isGeneral && (!currentUserId || event.managerId !== currentUserId)) {
      console.debug('[TelephonyStore] dropping call event not addressed to current user', {
        eventManagerId: event.managerId,
        currentUserId,
      })
      return
    }

    switch (event.type) {
      case 'CALL_RINGING':
        callState.value = 'ringing'
        callPhoneNumber.value = event.destination || event.callerId || ''
        callDisposition.value = ''
        callDuration.value = 0
        callIsGeneral.value = event.isGeneral === true
        if (event.callId && !activeCallResponse.value) {
          activeCallResponse.value = {
            callId: event.callId,
            pbxCallId: event.pbxCallId,
            status: 'ringing',
          }
        }
        break

      case 'CALL_ANSWERED':
        callState.value = 'answered'
        break

      case 'CALL_ENDED':
        callState.value = 'ended'
        callDisposition.value = event.disposition || ''
        callDuration.value = event.duration || 0
        setTimeout(() => {
          clearActiveCall()
        }, 3000)
        break

      case 'CALL_ANALYSIS_COMPLETED':
      case 'CALL_RECORD_READY':
        // AI-анализ завершён или запись готова — обновить список звонков и сбросить кеш транскрипции
        if (event.callId) {
          delete transcriptionCache.value[event.callId]
        }
        loadCalls()
        break
    }
  }

  /**
   * Завершить активный звонок (клиентское состояние)
   */
  const clearActiveCall = () => {
    activeCallResponse.value = null
    callState.value = 'idle'
    callPhoneNumber.value = ''
    callDisposition.value = ''
    callDuration.value = 0
    callIsGeneral.value = false
    activeCallClientId.value = null
    activeCallClientName.value = null
  }

  /**
   * Установить клиента для активного звонка.
   * Автоматически подтягивает имя клиента по ID если не передано.
   */
  const setActiveCallClient = (clientId: string, phoneNumber: string, clientName?: string) => {
    activeCallClientId.value = clientId
    activeCallClientName.value = clientName || null
    callPhoneNumber.value = phoneNumber
    callState.value = 'ringing'
    console.info('[TelephonyStore] Active call client set:', { clientId, phoneNumber, clientName })

    if (!clientName && clientId) {
      // DEV-2503. API клиентов грузится по требованию: статический импорт держал
      // его (и клиент задач) во входном чанке ради одного имени в виджете звонка.
      import('@/features/clients/api/clients.api')
        .then(({ clientsApi }) => clientsApi.getClientById(clientId))
        .then((client) => {
          if (activeCallClientId.value === clientId) {
            const name = [client.firstName, client.lastName].filter(Boolean).join(' ')
            if (name) {
              activeCallClientName.value = name
              console.info('[TelephonyStore] Client name resolved:', name)
            }
          }
        })
        .catch((e) => {
          console.warn('[TelephonyStore] Failed to fetch client name:', e)
        })
    }
  }

  /**
   * Обновить фильтры
   */
  const setFilters = (newFilters: CallsFilter) => {
    filters.value = newFilters
    currentPage.value = 1
  }

  /**
   * Сброс состояния
   */
  const $reset = () => {
    calls.value = []
    currentCall.value = null
    activeCallResponse.value = null
    isLoading.value = false
    isLoadingCalls.value = false
    isInitiatingCall.value = false
    error.value = null
    currentPage.value = 1
    totalCalls.value = 0
    totalPages.value = 0
    filters.value = {}
    webrtcKey.value = null
    managerStatus.value = null
    servicePhones.value = []
    isLoadingPhones.value = false
    transcriptionCache.value = {}
    isLoadingTranscription.value = false
    callState.value = 'idle'
    callPhoneNumber.value = ''
    callDisposition.value = ''
    callDuration.value = 0
    activeCallClientId.value = null
    activeCallClientName.value = null
  }

  return {
    // State
    calls,
    currentCall,
    activeCallResponse,
    isLoading,
    isLoadingCalls,
    isInitiatingCall,
    error,
    currentPage,
    pageSize,
    totalCalls,
    totalPages,
    filters,
    webrtcKey,
    managerStatus,
    servicePhones,
    isLoadingPhones,
    transcriptionCache,
    isLoadingTranscription,
    callState,
    callPhoneNumber,
    callDisposition,
    callDuration,
    callIsGeneral,
    activeCallClientId,
    activeCallClientName,

    // Getters
    hasCalls,
    hasMorePages,
    isCallActive,
    hasServicePhones,

    // Actions
    loadCalls,
    loadCallsByClient,
    initiateCall,
    loadServicePhones,
    getCallRecording,
    fetchWebRTCKey,
    fetchManagerStatus,
    getCallTranscription,
    handleCallEvent,
    clearActiveCall,
    setActiveCallClient,
    setFilters,
    $reset,
  }
})
